import React from "react";
import { ArrowLeft } from "lucide-react";
import { LogoFilterDefs } from "../components/landing/Logo";

/**
 * Vexly One Privacy Policy — public legal page required by Apple App Store
 * and Google Play. Pure text content, bilingual (ES then EN).
 * Route: /one/privacidad
 */
export default function VexlyOnePrivacy() {
  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white antialiased">
      <LogoFilterDefs />

      <header className="border-b border-white/5">
        <div className="max-w-[760px] mx-auto px-5 md:px-8 h-16 flex items-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={15} />
            vexlyapp.com
          </a>
        </div>
      </header>

      <main className="max-w-[760px] mx-auto px-5 md:px-8 py-10 md:py-16 text-[15px] md:text-base leading-[1.75] text-white/80">
        {/* ===== Español ===== */}
        <article lang="es">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white mb-2">
            Política de Privacidad de Vexly One
          </h1>
          <p className="text-sm text-white/50 mb-10">
            Vigente desde: 2 de octubre de 2026
          </p>

          <Section title="1. Quiénes somos">
            Vexly One es una aplicación de ventas y facturación electrónica
            (e-CF) para negocios en la República Dominicana, operada por
            BRIGHTWAY SOLUTIONS LLC, 109 Fulton Ct, New Brunswick, NJ 08901,
            Estados Unidos. Contacto:{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
            >
              soporte@vexlyapp.com
            </a>
            .
          </Section>

          <Section title="2. A quién aplica">
            Vexly One la usan negocios (“la Empresa”) y su personal. La Empresa
            decide qué datos de sus clientes registra y es responsable de
            ellos; nosotros los procesamos solo para prestar el servicio.
          </Section>

          <Section title="3. Datos que recopilamos">
            <ul className="list-disc pl-6 space-y-2 marker:text-brand-orange/70">
              <li>
                Cuenta de usuario: nombre, usuario y contraseña (guardada
                cifrada con hash, nunca en texto).
              </li>
              <li>
                Datos de la Empresa: nombre, RNC, dirección, teléfono, correo
                y configuración de facturación electrónica. Las credenciales
                del proveedor de facturación se guardan cifradas.
              </li>
              <li>
                Clientes de la Empresa: nombre, RNC o cédula, teléfono, correo
                y tipo de comprobante, cuando la Empresa los registra.
              </li>
              <li>
                Operaciones: productos, ventas, pagos, comprobantes fiscales
                electrónicos (XML) y respuestas de la DGII.
              </li>
              <li>
                Fotos: solo las que el usuario decida tomar o adjuntar. La app
                pide permiso de cámara o fotos únicamente en ese momento.
              </li>
            </ul>
          </Section>

          <Section title="4. Para qué los usamos">
            Para operar la app: registrar ventas, emitir y enviar comprobantes
            fiscales electrónicos a la DGII, enviar comprobantes por correo
            cuando el usuario lo pide, generar reportes y dar soporte. No
            vendemos datos, no mostramos publicidad y no usamos herramientas
            de rastreo ni analítica de terceros.
          </Section>

          <Section title="5. Con quién los compartimos">
            <ul className="list-disc pl-6 space-y-2 marker:text-brand-orange/70">
              <li>
                Dirección General de Impuestos Internos (DGII), a través de
                nuestro proveedor de servicios de facturación electrónica,
                para emitir los e-CF.
              </li>
              <li>
                Nuestro proveedor de correo electrónico, para enviar los
                comprobantes por correo.
              </li>
              <li>
                Nuestro proveedor de alojamiento en la nube, que guarda la
                información.
              </li>
              <li>Autoridades, cuando la ley lo exija.</li>
            </ul>
          </Section>

          <Section title="6. Cuánto tiempo los guardamos">
            Mientras la cuenta esté activa. Los comprobantes fiscales y
            registros de ventas se conservan por el plazo que exige la ley
            tributaria dominicana (10 años, Código Tributario, art. 50, literal
            h), aun si se cierra la cuenta. Los demás datos personales se
            eliminan o anonimizan al cerrar la cuenta.
          </Section>

          <Section title="7. Tus derechos">
            Puedes pedir acceso, corrección o eliminación de tus datos,
            conforme a la Ley 172-13 de la República Dominicana, escribiendo a{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
            >
              soporte@vexlyapp.com
            </a>
            . Si eres cliente de un negocio que usa Vexly One, también puedes
            dirigirte a ese negocio.
          </Section>

          <Section title="8. Eliminar la cuenta">
            Puedes eliminar tu cuenta desde la app (Ajustes → Eliminar mi
            cuenta) o pedirlo en{" "}
            <a
              href="/one/eliminar-cuenta"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
              data-testid="privacy-delete-link-es"
            >
              vexlyapp.com/one/eliminar-cuenta
            </a>{" "}
            o escribiendo a{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
            >
              soporte@vexlyapp.com
            </a>
            . Conservaremos solo los registros fiscales que la ley obliga a
            guardar.
          </Section>

          <Section title="9. Seguridad">
            Usamos conexiones cifradas (HTTPS), contraseñas con hash y
            credenciales cifradas. Ningún sistema es 100% seguro, pero
            protegemos la información con medidas razonables.
          </Section>

          <Section title="10. Menores de edad">
            Vexly One es una herramienta para negocios y no está dirigida a
            menores de 18 años.
          </Section>

          <Section title="11. Cambios">
            Si cambiamos esta política, publicaremos la nueva versión en esta
            página con su fecha.
          </Section>

          <Section title="12. Contacto">
            BRIGHTWAY SOLUTIONS LLC —{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
            >
              soporte@vexlyapp.com
            </a>
          </Section>
        </article>

        <hr className="my-16 border-white/10" />

        {/* ===== English ===== */}
        <article lang="en">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white mb-2">
            Vexly One Privacy Policy
          </h1>
          <p className="text-sm text-white/50 mb-10">
            Effective: October 2, 2026
          </p>

          <Section title="1. Who we are">
            Vexly One is a sales and electronic invoicing (e-CF) app for
            businesses in the Dominican Republic, operated by BRIGHTWAY
            SOLUTIONS LLC, 109 Fulton Ct, New Brunswick, NJ 08901, USA.
            Contact:{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
            >
              soporte@vexlyapp.com
            </a>
            .
          </Section>

          <Section title="2. Who this applies to">
            Vexly One is used by businesses (“the Business”) and their staff.
            The Business decides which customer data it records and is
            responsible for it; we process it only to provide the service.
          </Section>

          <Section title="3. Data we collect">
            <ul className="list-disc pl-6 space-y-2 marker:text-brand-orange/70">
              <li>
                User account: name, username and password (stored hashed,
                never in plain text).
              </li>
              <li>
                Business data: name, tax ID (RNC), address, phone, email and
                e-invoicing settings. Invoicing provider credentials are stored
                encrypted.
              </li>
              <li>
                The Business’s customers: name, RNC or national ID (cédula),
                phone, email and invoice type, when the Business records them.
              </li>
              <li>
                Transactions: products, sales, payments, electronic tax
                receipts (XML) and responses from the Dominican tax authority
                (DGII).
              </li>
              <li>
                Photos: only those the user chooses to take or attach. The app
                asks for camera or photo access only at that moment.
              </li>
            </ul>
          </Section>

          <Section title="4. How we use it">
            To run the app: record sales, issue and send electronic tax
            receipts to the DGII, email receipts when the user requests it,
            produce reports and provide support. We do not sell data, show
            ads, or use third-party tracking or analytics tools.
          </Section>

          <Section title="5. Who we share it with">
            <ul className="list-disc pl-6 space-y-2 marker:text-brand-orange/70">
              <li>
                The DGII, through our electronic invoicing service provider,
                to issue e-CF.
              </li>
              <li>
                Our email service provider, to send receipts by email.
              </li>
              <li>
                Our cloud hosting provider, which stores the information.
              </li>
              <li>Authorities, when required by law.</li>
            </ul>
          </Section>

          <Section title="6. How long we keep it">
            While the account is active. Tax receipts and sales records are
            kept for the period required by Dominican tax law (10 years, Tax
            Code, art. 50(h)), even if the account is closed. Other personal
            data is deleted or anonymized when the account is closed.
          </Section>

          <Section title="7. Your rights">
            You may request access to, correction of, or deletion of your data
            under Dominican Law 172-13 by writing to{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
            >
              soporte@vexlyapp.com
            </a>
            . If you are a customer of a business that uses Vexly One, you may
            also contact that business.
          </Section>

          <Section title="8. Account deletion">
            You can delete your account in the app (Settings → Delete my
            account), or request it at{" "}
            <a
              href="/one/eliminar-cuenta"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
              data-testid="privacy-delete-link-en"
            >
              vexlyapp.com/one/eliminar-cuenta
            </a>{" "}
            or by emailing{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
            >
              soporte@vexlyapp.com
            </a>
            . We will keep only the tax records the law requires us to retain.
          </Section>

          <Section title="9. Security">
            We use encrypted connections (HTTPS), hashed passwords and
            encrypted credentials. No system is 100% secure, but we protect
            information with reasonable measures.
          </Section>

          <Section title="10. Children">
            Vexly One is a business tool and is not directed to anyone under
            18.
          </Section>

          <Section title="11. Changes">
            If we change this policy, we will post the new version on this
            page with its date.
          </Section>

          <Section title="12. Contact">
            BRIGHTWAY SOLUTIONS LLC —{" "}
            <a
              href="mailto:soporte@vexlyapp.com"
              className="text-brand-orange hover:text-brand-orangeHover underline-grow"
            >
              soporte@vexlyapp.com
            </a>
          </Section>
        </article>

        <footer className="mt-16 pt-6 border-t border-white/5 text-xs text-white/40">
          <p>
            © 2026 BRIGHTWAY SOLUTIONS LLC. All rights reserved. ·{" "}
            <a href="/" className="hover:text-white underline-grow">
              vexlyapp.com
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-heading text-xl md:text-2xl font-semibold text-white mb-3 tracking-tight">
        {title}
      </h2>
      <div className="text-white/75">{children}</div>
    </section>
  );
}
